// ══════════════════════════════════════════════════════════════════════════════
//  src/components/teacher/PortalProfesor.jsx
//  EL PORTAL DEL PROFESOR — su agenda, sus alumnos, su evaluacion
//
//  ── QUE ES ESTO Y QUE NO ES ─────────────────────────────────────────────────
//  Rob lo describio asi: «el teacher entra como alumno, ve todo y es normal la
//  plataforma; lo que pasa es que ademas puede ver una interfaz o boton de
//  horarios y alumnos, donde puede ver las clases, posponer —avisando con 24
//  horas— y calificar a un alumno su desempeño».
//
//  Es decir: **esto se suma a lo que ya tiene, no lo sustituye.** El profesor
//  sigue entrando al gimnasio como cualquiera —esa fue la conclusion del
//  experimento 3.5(c)— y ademas tiene esta puerta. Por eso el portal no se
//  come la cabecera ni cambia la navegacion: es una pantalla mas.
//
//  ── QUE NO SALE AQUI, A PROPOSITO ───────────────────────────────────────────
//  Dinero. Ni el plan que paga cada alumno, ni cuanto, ni la facturacion. El
//  servidor tampoco lo manda. Un profesor no necesita eso para dar clase, y
//  enseñarlo seria repartir datos del negocio sin motivo.
//
//  Tampoco sale ningun alumno que no sea suyo, ni ninguna clase de otro
//  profesor — y eso no depende de esta pantalla: `/api/teacher` no acepta un
//  `teacher_id`, asi que no hay forma de pedirlo aunque se quisiera.
// ══════════════════════════════════════════════════════════════════════════════

import { useMemo, useState } from 'react';
import './portalProfesor.css';
import {
  useAgenda, useCambioVisto, useClasesEmpresa, useContrato, useFirmarContrato, useGanancias,
  useMisAlumnos, usePedirCobro, useProponerPrecio, useYo,
} from './useProfesor.js';
import ClasesEmpresa from './ClasesEmpresa.jsx';
import ModalEvaluar from './ModalEvaluar.jsx';
import ModalHistorial from './ModalHistorial.jsx';
// `ModalPosponer` y `ModalRenunciar` cambiaban la clase directo, sin que el
// alumno aceptara nada. Ahora todo cambio se pide a gerencia por WhatsApp.
import ModalAvisarGerencia from './ModalAvisarGerencia.jsx';

const NOMBRE_DEL_ESTADO = {
  assigned:  'Asignada',
  completed: 'Dada',
  cancelled: 'Cancelada',
  postponed: 'Pospuesta',
  // Las dos caras del mismo movimiento: el profesor dice que no puede
  // («soltada») y gerencia se la pasa a otro («traspasada»). Ninguna de las dos
  // le cuenta ya, pero las dos se siguen viendo: son su prueba de que avisó.
  released:    'Soltada',
  transferred: 'Traspasada',
};

/** El mes actual en «2026-08», que es lo que acepta la agenda. */
function mesDeHoy() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** «2026-08» → «agosto 2026», para el titulo. */
function mesEnPalabras(mes) {
  try {
    const [y, m] = mes.split('-').map(Number);
    return new Date(y, m - 1, 1)
      .toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
  } catch {
    return mes;
  }
}

/** «2026-10-11» → «11 de octubre». Al mediodía, para que ningún huso horario lo mueva de día. */
function fechaLarga(iso) {
  return new Date(`${iso}T12:00:00`).toLocaleDateString('es-MX', { day: 'numeric', month: 'long' });
}

function corre(mes, pasos) {
  const [y, m] = mes.split('-').map(Number);
  const d = new Date(y, m - 1 + pasos, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function PortalProfesor() {
  const [pestana, setPestana] = useState('agenda');
  const [mes, setMes]         = useState(mesDeHoy);

  // Cada modal guarda el objeto entero, no solo un id: asi el titulo puede
  // decir «Evaluar a Kamy» sin volver a buscar quien es Kamy.
  const [evaluando, setEvaluando]   = useState(null);   // { alumno, clase? }
  const [moviendo, setMoviendo]     = useState(null);   // clase
  const [historial, setHistorial]   = useState(null);   // alumno
  const [soltando, setSoltando]     = useState(null);   // clase

  const yo       = useYo();
  const agenda   = useAgenda(mes);
  const alumnos  = useMisAlumnos();
  const contrato = useContrato();
  // La misma consulta que usa la pestaña (misma clave): aquí solo se lee cuántas
  // clases de empresa tiene sin reporte, para avisar desde la pestaña cerrada.
  const empresas = useClasesEmpresa(mes);
  const sinReporte = empresas.data?.total_pendientes || 0;
  // Lo que gerencia le movió y aún no ha visto: va arriba de todo, se abra la
  // pestaña que se abra. Rob, 19-09-2026: «si un teacher no me ve en WhatsApp,
  // debería indicar el sistema ese cambio».
  const cambios = empresas.data?.cambios || [];
  const visto = useCambioVisto();

  const dias = useMemo(() => {
    const porDia = agenda.data?.clases_por_dia || {};
    return Object.keys(porDia).sort();
  }, [agenda.data]);

  if (yo.isLoading) {
    return <div className="pp-root"><div className="pp-cargando">Abriendo tu portal…</div></div>;
  }

  if (yo.isError) {
    return (
      <div className="pp-root">
        <div className="pp-wrap">
          <div className="pp-vacio">
            <div className="pp-vacio-t">No pudimos abrir tu portal</div>
            <div className="pp-vacio-s">{yo.error?.message}</div>
          </div>
        </div>
      </div>
    );
  }

  const p = yo.data;
  const listaAlumnos = alumnos.data || [];
  const horasMinimas = agenda.data?.horas_minimas_para_posponer ?? 24;

  return (
    <div className="pp-root">
      <div className="pp-wrap">

        {/* ─── QUIEN ERES ─────────────────────────────────────────────── */}
        <header className="pp-head">
          <div>
            <div className="pp-kicker">Portal del profesor</div>
            <h1 className="pp-nombre">{p.nombre}</h1>
            <div className="pp-sub">{p.lang} · {p.timezone}</div>
          </div>

          <div className="pp-pills">
            <div className="pp-pill">
              <div className="pp-pill-n">
                {p.alumnos.asignados}
                <span style={{ opacity: .4, fontSize: '.75em' }}>/{p.alumnos.maximo}</span>
              </div>
              <div className="pp-pill-t">Alumnos</div>
            </div>
            <div className="pp-pill">
              <div className="pp-pill-n">{p.clases_totales}</div>
              <div className="pp-pill-t">Clases</div>
            </div>
            {p.plan.tokens_max > 0 && (
              <div className={`pp-pill${p.plan.pide_ampliacion ? ' pp-alerta' : ''}`}>
                <div className="pp-pill-n">
                  {p.plan.tokens_restantes.toLocaleString('es-MX')}
                  <span style={{ opacity: .4, fontSize: '.75em' }}>/{p.plan.tokens_max.toLocaleString('es-MX')}</span>
                </div>
                <div className="pp-pill-t">Energía Spacy</div>
                {/* Se renuevan el día de su inscripción, no el 1 de cada mes. */}
                {p.plan.renueva && (
                  <div className="pp-pill-r">Se renuevan el {fechaLarga(p.plan.renueva)}</div>
                )}
              </div>
            )}
          </div>
        </header>

        {cambios.length > 0 && (
          <section className="pp-card pp-cambios" data-prueba="cambios-profesor">
            <div className="pp-card-t">
              📅 {cambios.length === 1 ? 'Gerencia cambió una de tus clases' : `Gerencia cambió ${cambios.length} de tus clases`}
            </div>
            <div className="pp-lista">
              {cambios.map((cb) => (
                <div key={cb.id} className={`pp-cambio${cb.tipo === 'cancelada' || cb.tipo === 'quitada' ? ' pp-cambio-er' : ''}`}
                  data-prueba="cambio-profesor" data-cambio={cb.id}>
                  <div className="pp-cambio-txt">
                    <strong>{cb.titulo}</strong>
                    <span>{cb.mensaje}</span>
                  </div>
                  <button className="pp-btn pp-btn-ac" disabled={visto.isPending}
                    onClick={() => visto.mutate(cb.id)} data-prueba="cambio-profesor-entendido">
                    Entendido
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ─── EL AVISO LLEGA ANTES, NO DESPUES ───────────────────────────
            Un profesor sin Energia Spacy no puede renovarse solo: tiene que pedir
            ampliacion a gerencia, y eso tarda. Enterarse cuando ya se ha
            quedado fuera convierte un limite en un incidente.              */}
        {p.plan.pide_ampliacion && (
          <div className="pp-aviso">
            <span aria-hidden="true">⚠️</span>
            <span>
              Te quedan <strong>{p.plan.tokens_restantes.toLocaleString('es-MX')}</strong> de
              {' '}{p.plan.tokens_max.toLocaleString('es-MX')} de Energía Spacy de tu
              plan <strong>{p.plan.nombre}</strong> en este ciclo. Pide una ampliación a
              gerencia antes de quedarte sin
              {p.plan.renueva ? <> — se renuevan el {fechaLarga(p.plan.renueva)}.</> : '.'}
            </span>
          </div>
        )}

        {/* ─── PESTANAS ───────────────────────────────────────────────── */}
        <nav className="pp-tabs">
          <button
            className={`pp-tab${pestana === 'agenda' ? ' pp-on' : ''}`}
            onClick={() => setPestana('agenda')}
          >
            Mis clases
          </button>
          {/* Las clases de empresa: grupos de 10, reporte por persona. Van en su
              pestaña porque no son de la agenda de «Mis clases» (una clase, un
              alumno) y el reporte es otro. El número rojo = sin reporte. */}
          <button
            className={`pp-tab${pestana === 'empresas' ? ' pp-on' : ''}`}
            onClick={() => setPestana('empresas')}
          >
            Empresas
            {sinReporte > 0 && (
              <span className="pp-tab-n" aria-label={`${sinReporte} sin reporte`}>{sinReporte}</span>
            )}
          </button>
          <button
            className={`pp-tab${pestana === 'alumnos' ? ' pp-on' : ''}`}
            onClick={() => setPestana('alumnos')}
          >
            Mis alumnos
          </button>
          <button
            className={`pp-tab${pestana === 'ganancias' ? ' pp-on' : ''}`}
            onClick={() => setPestana('ganancias')}
          >
            Mis ganancias
          </button>
          {/* El punto rojo no es decoración: sin firmar no se puede cobrar, y
              enterarse de eso al pulsar «Cobrar» es enterarse tarde. */}
          <button
            className={`pp-tab${pestana === 'acuerdo' ? ' pp-on' : ''}`}
            onClick={() => setPestana('acuerdo')}
          >
            Mi acuerdo
            {contrato.data && !contrato.data.firmado && (
              <span className="pp-sinfirmar" aria-label="sin firmar">●</span>
            )}
          </button>
        </nav>

        {/* ══ AGENDA ══════════════════════════════════════════════════ */}
        {pestana === 'agenda' && (
          <>
            {/* Lo que viene, primero. Es lo que se abre a mirar por la manana. */}
            {(agenda.data?.proximas || []).length > 0 && (
              <section className="pp-card">
                <div className="pp-card-h">
                  <div>
                    <div className="pp-card-t">Lo que viene</div>
                    <div className="pp-card-s">
                      Tus proximas clases asignadas, sin importar el mes que estes mirando.
                    </div>
                  </div>
                </div>
                <div className="pp-lista">
                  {agenda.data.proximas.map((c) => (
                    <div className="pp-clase" key={`prox-${c.id}`}>
                      <span className="pp-clase-fecha">{c.fecha}</span>
                      <span className="pp-clase-alumno">{c.alumno}</span>
                      <span className="pp-clase-hora">{c.hora}:00</span>
                      <span className={`pp-estado ${c.estado}`}>
                        {NOMBRE_DEL_ESTADO[c.estado] || c.estado}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className="pp-card">
              <div className="pp-card-h">
                <div>
                  <div className="pp-card-t" style={{ textTransform: 'capitalize' }}>
                    {mesEnPalabras(mes)}
                  </div>
                  <div className="pp-card-s">
                    {agenda.data?.resumen
                      ? `${agenda.data.resumen.assigned} asignadas · ${agenda.data.resumen.completed} dadas · ${agenda.data.resumen.postponed} pospuestas`
                      : ' '}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="pp-btn" onClick={() => setMes(corre(mes, -1))}>
                    ← Anterior
                  </button>
                  <button className="pp-btn" onClick={() => setMes(corre(mes, 1))}>
                    Siguiente →
                  </button>
                </div>
              </div>

              {agenda.isLoading && <div className="pp-cargando">Cargando agenda…</div>}

              {agenda.isError && <div className="pp-error">{agenda.error?.message}</div>}

              {!agenda.isLoading && !agenda.isError && dias.length === 0 && (
                <div className="pp-vacio">
                  <div className="pp-vacio-t">Ninguna clase este mes</div>
                  <div className="pp-vacio-s">
                    Las clases las asigna gerencia desde el panel. Si esperabas
                    alguna aqui, avisales.
                  </div>
                </div>
              )}

              {dias.map((dia) => (
                <div key={dia} style={{ marginBottom: 18 }}>
                  <div className="pp-label" style={{ marginBottom: 8 }}>{dia}</div>
                  <div className="pp-lista">
                    {agenda.data.clases_por_dia[dia].map((c) => (
                      <div className="pp-clase" key={c.id}>
                        <span className="pp-clase-hora" style={{ minWidth: 56 }}>
                          {c.hora}:00
                        </span>
                        <span className="pp-clase-alumno">{c.alumno}</span>
                        <span className={`pp-estado ${c.estado}`}>
                          {NOMBRE_DEL_ESTADO[c.estado] || c.estado}
                        </span>

                        {/* NINGUNO DE LOS DOS CAMBIA LA CLASE. Abren WhatsApp
                            con el mensaje para gerencia, que habla con el alumno y
                            hace el ajuste en el CRM. Por eso se pueden pulsar
                            aunque falten menos de 24 horas: avisar no cuesta nada,
                            y el aviso lo dice. */}
                        <button
                          className="pp-btn"
                          disabled={c.estado !== 'assigned'}
                          title={
                            c.estado !== 'assigned'
                              ? 'Solo se piden cambios de clases asignadas'
                              : 'Pedir a gerencia un cambio de fecha, por WhatsApp'
                          }
                          onClick={() => setMoviendo(c)}
                        >
                          Pedir cambio
                        </button>

                        <button
                          className="pp-btn"
                          disabled={c.estado !== 'assigned'}
                          title={
                            c.estado !== 'assigned'
                              ? 'Solo se avisan clases asignadas'
                              : 'Avisar a gerencia por WhatsApp de que no puedes darla'
                          }
                          onClick={() => setSoltando(c)}
                        >
                          No puedo
                        </button>

                        <button
                          className="pp-btn pp-btn-ac"
                          disabled={!c.alumno_id}
                          onClick={() => setEvaluando({
                            alumno: {
                              id: c.alumno_id,
                              nombre: c.alumno,
                            },
                            clase: c,
                          })}
                        >
                          Evaluar
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          </>
        )}

        {/* ══ GANANCIAS ═══════════════════════════════════════════════
            La segunda linea de ingreso del profesor: lo que gana por los
            alumnos que trae, aparte de lo que cobra por clases.

            AQUI NO SE CALCULA NADA. Ni se suma, ni se convierte, ni se
            redondea: todas las cifras llegan calculadas del servidor. Sumar
            pesos con dolares en la pantalla daria un numero que parece
            correcto y no lo es, y el profesor lo leeria como su dinero.  */}
        {pestana === 'ganancias' && (
          <Ganancias onIrAlAcuerdo={() => setPestana('acuerdo')} />
        )}

        {/* ══ CLASES DE EMPRESA ═══════════════════════════════════════════ */}
        {pestana === 'empresas' && (
          <ClasesEmpresa
            mes={mes}
            tituloMes={mesEnPalabras(mes)}
            onAnterior={() => setMes(corre(mes, -1))}
            onSiguiente={() => setMes(corre(mes, 1))}
          />
        )}

        {/* ══ MI ACUERDO ══════════════════════════════════════════════
            Lo que firma y lo que cobra. Las dos cosas que hace una vez y de
            las que depende todo el dinero que pase por aqui despues.  */}
        {pestana === 'acuerdo' && <Acuerdo precio={p.precio} />}

        {/* ══ ALUMNOS ═════════════════════════════════════════════════ */}
        {pestana === 'alumnos' && (
          <section className="pp-card">
            <div className="pp-card-h">
              <div>
                <div className="pp-card-t">Mis alumnos</div>
                <div className="pp-card-s">
                  Solo los asignados a ti. No ves ni su plan ni lo que pagan.
                </div>
              </div>
            </div>

            {alumnos.isLoading && <div className="pp-cargando">Cargando…</div>}

            {alumnos.isError && <div className="pp-error">{alumnos.error?.message}</div>}

            {!alumnos.isLoading && !alumnos.isError && listaAlumnos.length === 0 && (
              <div className="pp-vacio">
                <div className="pp-vacio-t">Todavia no tienes alumnos asignados</div>
                <div className="pp-vacio-s">
                  Gerencia los asigna desde el panel. En cuanto lo hagan, apareceran aqui.
                </div>
              </div>
            )}

            <div className="pp-grid">
              {listaAlumnos.map((a) => (
                <article className="pp-alumno" key={a.id}>
                  <div>
                    <div className="pp-alumno-n">{a.nombre}</div>
                    <div className="pp-alumno-datos" style={{ marginTop: 8 }}>
                      <span>NIVEL <b>{a.nivel}</b></span>
                      <span>XP <b>{(a.xp || 0).toLocaleString('es-MX')}</b></span>
                      <span>CLASES <b>{a.clases_conmigo}</b></span>
                    </div>
                    <div className="pp-alumno-datos" style={{ marginTop: 6 }}>
                      <span>
                        {a.evaluaciones_mias > 0
                          ? `${a.evaluaciones_mias} EVALUACION${a.evaluaciones_mias === 1 ? '' : 'ES'} TUYAS`
                          : 'SIN EVALUAR'}
                      </span>
                    </div>
                  </div>

                  <div className="pp-alumno-btns">
                    <button className="pp-btn" onClick={() => setHistorial(a)}>
                      Historial
                    </button>
                    <button
                      className="pp-btn pp-btn-ac"
                      onClick={() => setEvaluando({ alumno: a })}
                    >
                      Evaluar
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}
      </div>

      {evaluando && (
        <ModalEvaluar
          alumno={evaluando.alumno}
          clase={evaluando.clase}
          onCerrar={() => setEvaluando(null)}
        />
      )}

      {moviendo && (
        <ModalAvisarGerencia
          modo="mover"
          clase={moviendo}
          profesor={p.nombre}
          horasMinimas={horasMinimas}
          onCerrar={() => setMoviendo(null)}
        />
      )}

      {historial && (
        <ModalHistorial alumno={historial} onCerrar={() => setHistorial(null)} />
      )}

      {soltando && (
        <ModalAvisarGerencia
          modo="no_puedo"
          clase={soltando}
          profesor={p.nombre}
          horasMinimas={horasMinimas}
          onCerrar={() => setSoltando(null)}
        />
      )}
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════════════
   MIS GANANCIAS

   Lo que el profesor gana por los alumnos que trae. Es la primera pantalla de
   NSL donde alguien que no es gerencia ve dinero suyo, y eso cambia el listón:
   una cifra confusa aquí no es un detalle de diseño, es una discusión.

   Por eso se enseña SIEMPRE de dónde sale cada peso —la base, el porcentaje y
   el estado de cada movimiento— y por eso el saldo va separado por moneda
   aunque quede menos vistoso. Un total que junta pesos y dólares parece más
   limpio y es mentira.
   ═══════════════════════════════════════════════════════════════════════════ */

// ── LO QUE COBRA POR SUS CLASES ─────────────────────────────────────────────
//  Lo primero que quiere saber quien trabaja: cuánto lleva esta semana. Estaba
//  solo en la nómina de gerencia, así que el profesor lo preguntaba por
//  WhatsApp cada semana (22-09-2026).
//
//  Es un ESPEJO de lo que ya calcula la nómina, no una cuenta nueva: las mismas
//  clases dadas y la misma tarifa. Por eso se dice en la propia tarjeta que lo
//  confirma gerencia — y por eso, si gerencia ya lo pagó, esta tarjeta lo dice
//  en lugar del estimado.
function PagoDeClases({ clases }) {
  if (!clases) return null;

  const dinero = (n) =>
    `$${Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })} ${clases.moneda || 'MXN'}`;

  if (clases.sin_tarifa) {
    return (
      <section className="pp-card" data-prueba="pago-clases">
        <div className="pp-card-h">
          <div>
            <div className="pp-card-t">Lo que cobras por tus clases</div>
            <div className="pp-card-s">Todavía no tienes tarifa puesta.</div>
          </div>
        </div>
        <div className="pp-vacio">
          <div className="pp-vacio-t">Gerencia tiene que ponerte tu tarifa por clase</div>
          <div className="pp-vacio-s">
            Hasta entonces no podemos decirte cuánto te toca —ni pagártelo—.
            Escríbele y lo deja puesto en un minuto.
          </div>
        </div>
      </section>
    );
  }

  const pago = clases.pago;
  const pagada = pago && pago.estado === 'paid';

  return (
    <section className="pp-card" data-prueba="pago-clases">
      <div className="pp-card-h">
        <div>
          <div className="pp-card-t">Lo que cobras por tus clases</div>
          <div className="pp-card-s">
            {dinero(clases.tarifa)} por clase dada
          </div>
        </div>
      </div>

      <div className="pp-pago-rejilla">
        <div className="pp-pago-celda" data-prueba="pago-semana">
          <div className="pp-pago-n">{dinero(clases.semana.importe)}</div>
          <div className="pp-pago-t">Esta semana</div>
          <div className="pp-pago-s">
            {clases.semana.clases} {clases.semana.clases === 1 ? 'clase dada' : 'clases dadas'}
          </div>
        </div>
        <div className="pp-pago-celda">
          <div className="pp-pago-n">{dinero(clases.mes.importe)}</div>
          <div className="pp-pago-t">Este mes</div>
          <div className="pp-pago-s">
            {clases.mes.clases} {clases.mes.clases === 1 ? 'clase dada' : 'clases dadas'}
          </div>
        </div>
      </div>

      {pagada ? (
        <div className="pp-pago-estado pp-pago-ok" data-prueba="pago-estado">
          ✅ Gerencia ya pagó esta semana: {dinero(pago.total)}
          {pago.metodo ? ` · ${pago.metodo}` : ''}
        </div>
      ) : pago && pago.abonado > 0 ? (
        <div className="pp-pago-estado" data-prueba="pago-estado">
          ◑ Gerencia te abonó {dinero(pago.abonado)} de {dinero(pago.total)}.
          Quedan {dinero(pago.pendiente)}.
        </div>
      ) : (
        <div className="pp-pago-estado" data-prueba="pago-estado">{clases.nota}</div>
      )}
    </section>
  );
}


function Ganancias({ onIrAlAcuerdo }) {
  const g = useGanancias();
  const cobro = usePedirCobro();
  const [enlace, setEnlace] = useState(null);
  // Por que NO se creo la peticion. El servidor contesta 200 con un motivo
  // —«sin_firmar», por ejemplo— en vez de un error, y sin esto la pantalla se
  // quedaba muda: se pulsaba «Cobrar» y no pasaba nada visible.
  const [motivo, setMotivo] = useState(null);

  if (g.isLoading) return <div className="pp-cargando">Cargando…</div>;
  if (g.isError) return <div className="pp-error">{g.error?.message}</div>;

  const d = g.data || {};

  // Todavia no es afiliado. No es un error ni un fallo suyo: es que gerencia
  // aun no le ha dado de alta, y decirlo con calma evita un mensaje a soporte.
  if (!d.afiliado) {
    return (
      <>
      <PagoDeClases clases={d.clases} />
      <section className="pp-card">
        <div className="pp-card-h">
          <div>
            <div className="pp-card-t">Mis ganancias</div>
            <div className="pp-card-s">
              Todavia no tienes codigo de afiliado.
            </div>
          </div>
        </div>
        <div className="pp-vacio">
          <div className="pp-vacio-t">Gerencia aun no te ha dado de alta</div>
          <div className="pp-vacio-s">
            El programa de afiliados te deja ganar un porcentaje de lo que
            paguen los alumnos que tu traigas. Escribe a gerencia si te
            interesa.
          </div>
        </div>
      </section>
      </>
    );
  }

  const monedas = Object.keys(d.por_moneda || {});
  const enlaceDeInvitacion =
    `${window.location.origin}/?ref=${d.afiliado.codigo}`;

  // El minimo lo dice el servidor. Escribir «200» aqui a mano seria tener el
  // numero en dos sitios, y el dia que Rob lo cambie la pantalla seguiria
  // prometiendo el viejo.
  const minimoMXN = ((d.minimo_cobro || {}).MXN ?? 200).toLocaleString('es-MX');

  const copia = () => {
    try { navigator.clipboard.writeText(enlaceDeInvitacion); } catch { /* sin permiso */ }
  };

  const pedir = (moneda) => {
    setEnlace(null);
    setMotivo(null);
    cobro.mutate(moneda, {
      onSuccess: (r) => {
        if (!r?.creada) { setMotivo(r?.motivo || 'desconocido'); return; }
        if (r?.creada && r.whatsapp) {
          setEnlace(r.whatsapp);
          // Se abre la conversacion ya escrita. Si el navegador la bloquea,
          // el enlace se queda a la vista debajo: nunca se pierde la peticion,
          // que ya quedo guardada en el servidor.
          window.open(r.whatsapp, '_blank', 'noopener');
        }
      },
    });
  };

  return (
    <>
      {/* Lo suyo por dar clase va primero: es su sueldo. El programa de
          referidos, que es un extra, va debajo. */}
      <PagoDeClases clases={d.clases} />

      {/* ─── SU CODIGO ─────────────────────────────────────────────── */}
      <section className="pp-card">
        <div className="pp-card-h">
          <div>
            <div className="pp-card-t">Mi enlace de invitacion</div>
            <div className="pp-card-s">
              Quien se registre con el queda tuyo, y ganas el{' '}
              <strong>{d.afiliado.porcentaje}%</strong> de lo que pague.
            </div>
          </div>
        </div>

        <div className="pp-codigo">
          <code>{enlaceDeInvitacion}</code>
          <button type="button" className="pp-btn" onClick={copia}>Copiar</button>
        </div>

        {d.afiliado.estado !== 'activo' && (
          <div className="pp-aviso" style={{ marginTop: 12 }}>
            <span aria-hidden="true">⚠️</span>
            <span>
              Tu codigo esta <strong>en pausa</strong>: los alumnos que se
              registren ahora no cuentan. Lo que ya ganaste sigue siendo tuyo.
            </span>
          </div>
        )}

        {/* ─── COMO SE COBRA ESTO ──────────────────────────────────────
            Rob: «enseñarles cómo cobran lo del código de referidos, al juntar
            200 lo pueden cobrar».

            Va aqui y no escondido en un mensaje de error, porque la pregunta
            «¿y esto cuándo lo veo?» se la hace el primer dia — cuando el saldo
            es cero y todavia no hay ningun boton apagado que la conteste. */}
        <ol className="pp-pasos">
          <li>
            <b>Reparte tu enlace.</b> Quien se registre con él queda asociado a
            ti para siempre.
          </li>
          <li>
            <b>Ganas cuando ese alumno paga</b>, no cuando se registra. Cada
            pago suyo te deja el {d.afiliado.porcentaje}%.
          </li>
          <li>
            <b>Al juntar {minimoMXN} MXN puedes cobrar.</b> Por debajo de esa
            cifra el saldo no se pierde: se queda acumulado para la siguiente.
          </li>
          <li>
            <b>Pulsas «Cobrar» y se abre un WhatsApp</b> con gerencia para que
            pases tu número de cuenta. NSL no guarda tus datos bancarios.
          </li>
        </ol>
      </section>

      {/* ─── EL SALDO, POR MONEDA ──────────────────────────────────── */}
      <section className="pp-card">
        <div className="pp-card-h">
          <div>
            <div className="pp-card-t">Lo que llevas ganado</div>
            <div className="pp-card-s">
              {d.alumnos_referidos === 1
                ? '1 alumno se registro con tu enlace.'
                : `${d.alumnos_referidos} alumnos se registraron con tu enlace.`}
            </div>
          </div>
        </div>

        {monedas.length === 0 && (
          <div className="pp-vacio">
            <div className="pp-vacio-t">Todavia no hay comisiones</div>
            <div className="pp-vacio-s">
              Una comision aparece cuando un alumno tuyo PAGA, no cuando se
              registra. Reparte tu enlace y espera al primer cobro.
            </div>
          </div>
        )}

        {monedas.map((m) => {
          const s2 = d.por_moneda[m];
          const c = (d.cobros || {})[m] || {};
          return (
            <div className="pp-saldo" key={m}>
              <div className="pp-saldo-cifras">
                <div>
                  <span className="pp-saldo-e">Por cobrar</span>
                  <b className="pp-saldo-n">
                    {s2.pendiente.toLocaleString('es-MX')} {m}
                  </b>
                </div>
                <div>
                  <span className="pp-saldo-e">Ya cobrado</span>
                  <b className="pp-saldo-n pp-suave">
                    {s2.pagada.toLocaleString('es-MX')} {m}
                  </b>
                </div>
              </div>

              <div className="pp-saldo-accion">
                <button
                  type="button"
                  className="pp-btn pp-btn-cobrar"
                  disabled={!c.puede || cobro.isPending}
                  onClick={() => pedir(m)}
                >
                  {cobro.isPending ? 'Pidiendo…' : `Cobrar ${m}`}
                </button>

                {/* POR QUE NO PUEDE, dicho con claridad. Un boton apagado sin
                    explicacion manda a escribir a soporte. */}
                {!c.puede && c.motivo === 'bajo_minimo' && (
                  <span className="pp-saldo-nota">
                    Se puede cobrar a partir de {c.minimo} {m}. Lo que llevas no
                    se pierde: se acumula.
                  </span>
                )}
                {!c.puede && c.motivo === 'ya_solicitado' && (
                  <span className="pp-saldo-nota">
                    Ya pediste este cobro (solicitud #{c.solicitud_id}).
                    Gerencia lo esta gestionando.
                  </span>
                )}
                {!c.puede && c.motivo === 'sin_saldo' && (
                  <span className="pp-saldo-nota">Nada pendiente de cobro.</span>
                )}
              </div>
            </div>
          );
        })}

        {cobro.isError && <div className="pp-error">{cobro.error?.message}</div>}

        {/* ─── NO SE PUDO PEDIR, Y POR QUE ─────────────────────────────
            El servidor contesta 200 con un motivo en vez de un error, asi que
            sin esto la pantalla se quedaba callada. Y el motivo mas probable
            —no haber firmado— tiene solucion en dos clics: se pone el camino,
            no solo el diagnostico. */}
        {motivo === 'sin_firmar' && (
          <div className="pp-aviso" style={{ marginTop: 12 }}>
            <span aria-hidden="true">✍️</span>
            <span>
              Antes de cobrar hay que firmar el acuerdo: ahí dice cómo y cuándo
              se paga.{' '}
              <button type="button" className="pp-enlace"
                      onClick={onIrAlAcuerdo}>
                Ir a mi acuerdo
              </button>
            </span>
          </div>
        )}
        {motivo && motivo !== 'sin_firmar' && (
          <div className="pp-aviso" style={{ marginTop: 12 }}>
            <span aria-hidden="true">ℹ️</span>
            <span>
              No se pudo crear la petición ({motivo}). Escribe a gerencia si
              crees que es un error.
            </span>
          </div>
        )}

        {enlace && (
          <div className="pp-aviso" style={{ marginTop: 12 }}>
            <span aria-hidden="true">✅</span>
            <span>
              Tu peticion quedo registrada.{' '}
              <a href={enlace} target="_blank" rel="noopener noreferrer">
                Abre WhatsApp
              </a>{' '}
              y pasa tus datos bancarios por ahi.
            </span>
          </div>
        )}
      </section>

      {/* ─── DE DONDE SALE CADA PESO ───────────────────────────────── */}
      {(d.movimientos || []).length > 0 && (
        <section className="pp-card">
          <div className="pp-card-h">
            <div>
              <div className="pp-card-t">Tus movimientos</div>
              <div className="pp-card-s">
                Cada comision, con el pago sobre el que se calculo. Si algo no
                cuadra, aqui esta el porque.
              </div>
            </div>
          </div>

          <div className="pp-movs">
            {d.movimientos.map((mv) => (
              <div className={`pp-mov pp-mov-${mv.estado}`} key={mv.id}>
                <span className="pp-mov-f">
                  {mv.cuando ? new Date(mv.cuando).toLocaleDateString('es-MX') : '—'}
                </span>
                <span className="pp-mov-d">
                  {mv.porcentaje}% de {mv.base.toLocaleString('es-MX')} {mv.moneda}
                </span>
                <span className="pp-mov-i">
                  {mv.importe.toLocaleString('es-MX')} {mv.moneda}
                </span>
                <span className="pp-mov-e">{mv.estado}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ─── SUS ALUMNOS ───────────────────────────────────────────── */}
      {(d.alumnos || []).length > 0 && (
        <section className="pp-card">
          <div className="pp-card-h">
            <div>
              <div className="pp-card-t">Alumnos que trajiste</div>
              <div className="pp-card-s">
                Solo su nombre y desde cuando. Ni su correo ni lo que paga.
              </div>
            </div>
          </div>
          <div className="pp-grid">
            {d.alumnos.map((a) => (
              <article className="pp-alumno" key={`${a.nombre}-${a.desde}`}>
                <div>
                  <div className="pp-alumno-n">{a.nombre}</div>
                  <div className="pp-alumno-datos" style={{ marginTop: 8 }}>
                    <span>
                      DESDE{' '}
                      <b>
                        {a.desde
                          ? new Date(a.desde).toLocaleDateString('es-MX')
                          : '—'}
                      </b>
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
    </>
  );
}


/* ══════════════════════════════════════════════════════════════════════════════
   MI ACUERDO

   Las dos cosas que un profesor hace una vez y de las que depende todo el
   dinero que pase por aqui despues: firmar lo que acepta, y decir cuanto quiere
   cobrar por hora.

   ── POR QUE EL TEXTO SE PIDE AL SERVIDOR ────────────────────────────────────
   Porque lo que se guarda de una firma es LA VERSION, no el texto. Si el
   acuerdo viviera escrito en esta pantalla, cambiarlo en el backend dejaria al
   profesor firmando un documento distinto del que consta que firmo — y una
   firma asi no vale nada.

   ── Y POR QUE EL PRECIO NO SE APLICA SOLO ───────────────────────────────────
   Lo que escribe aqui va a `precio_propuesto`, nunca a `precio_hora`. Sale a la
   vitrina cuando gerencia lo aprueba. Es decision de Rob, y es la correcta: el
   precio publico es dinero suyo tambien, y un campo que se publica solo es un
   campo por el que cualquiera puede poner 5.000 un domingo por la noche.
   ═══════════════════════════════════════════════════════════════════════════ */

function Acuerdo({ precio }) {
  const contrato = useContrato();
  const firmar = useFirmarContrato();
  const proponer = useProponerPrecio();

  const [abierto, setAbierto] = useState(false);
  const [pide, setPide] = useState('');

  if (contrato.isLoading) return <div className="pp-cargando">Cargando…</div>;
  if (contrato.isError) return <div className="pp-error">{contrato.error?.message}</div>;

  const doc = contrato.data?.documento || {};
  const firmado = Boolean(contrato.data?.firmado);
  const pr = precio || {};
  const moneda = pr.moneda || 'MXN';

  const cifra = Number(String(pide).replace(/[^0-9.]/g, ''));
  const puedePedir = cifra > 0 && cifra <= 10000 && !proponer.isPending;

  return (
    <>
      {/* ─── EL ACUERDO ────────────────────────────────────────────── */}
      <section className="pp-card">
        <div className="pp-card-h">
          <div>
            <div className="pp-card-t">{doc.titulo || 'Tu acuerdo con NSL'}</div>
            <div className="pp-card-s">{doc.resumen}</div>
          </div>
          <span className={`pp-firma${firmado ? ' pp-firma-ok' : ''}`}>
            {firmado ? '✓ Firmado' : 'Sin firmar'}
          </span>
        </div>

        {!firmado && (
          <div className="pp-aviso" style={{ marginTop: 12 }}>
            <span aria-hidden="true">⚠️</span>
            <span>
              Puedes dar clases mientras tanto, pero <strong>no puedes pedir
              un cobro</strong> hasta que lo firmes. Lo que ganes se sigue
              acumulando.
            </span>
          </div>
        )}

        <div className="pp-clausulas">
          {(doc.clausulas || []).slice(0, abierto ? 99 : 1).map((c) => (
            <div className="pp-clausula" key={c.titulo}>
              <b>{c.titulo}</b>
              <p>{c.texto}</p>
            </div>
          ))}
        </div>

        <button type="button" className="pp-btn"
                onClick={() => setAbierto((v) => !v)}>
          {abierto ? 'Ocultar' : `Leer las ${(doc.clausulas || []).length} condiciones`}
        </button>

        {!firmado && (
          <div className="pp-firmar">
            <button
              type="button"
              className="pp-btn pp-btn-ac"
              disabled={firmar.isPending}
              onClick={() => firmar.mutate(doc.version)}
            >
              {firmar.isPending ? 'Firmando…' : 'He leído y acepto'}
            </button>
            <span className="pp-nota">Versión {doc.version}</span>
          </div>
        )}

        {firmado && contrato.data?.firmado_at && (
          <div className="pp-nota" style={{ marginTop: 10 }}>
            Firmado el{' '}
            {new Date(contrato.data.firmado_at).toLocaleDateString('es-MX')} ·
            versión {doc.version}
          </div>
        )}

        {firmar.isError && <div className="pp-error">{firmar.error?.message}</div>}
      </section>

      {/* ─── SU PRECIO ─────────────────────────────────────────────── */}
      <section className="pp-card">
        <div className="pp-card-h">
          <div>
            <div className="pp-card-t">Tu precio por hora</div>
            <div className="pp-card-s">
              Tú propones cuánto cobras. Es lo que recibes por clase dada.
            </div>
          </div>
        </div>

        {!pr.tiene_ficha ? (
          <div className="pp-vacio">
            <div className="pp-vacio-t">Todavía no tienes ficha pública</div>
            <div className="pp-vacio-s">
              Gerencia la crea con tu biografía y tus especialidades. Cuando
              exista podrás proponer tu precio aquí.
            </div>
          </div>
        ) : (
          <>
            <div className="pp-precios">
              <div>
                <span className="pp-saldo-e">Publicado</span>
                <b className="pp-saldo-n">
                  {pr.vigente ? `${pr.vigente.toLocaleString('es-MX')} ${moneda}` : '—'}
                </b>
              </div>
              <div>
                <span className="pp-saldo-e">Lo que paga el alumno</span>
                <b className="pp-saldo-n pp-suave">
                  {pr.publico ? `${pr.publico.toLocaleString('es-MX')} ${moneda}` : '—'}
                </b>
              </div>
              {pr.propuesto ? (
                <div>
                  <span className="pp-saldo-e">Propuesto</span>
                  <b className="pp-saldo-n pp-suave">
                    {pr.propuesto.toLocaleString('es-MX')} {moneda}
                  </b>
                </div>
              ) : null}
            </div>

            {/* La diferencia entre las dos cifras es el margen de NSL, y se
                dice con todas las letras. El acuerdo ya lo explica; callarlo
                aqui solo conseguiria que lo calculara solo. */}
            <div className="pp-nota" style={{ marginTop: 8 }}>
              La diferencia es la gestión de NSL: cobro, garantía de la clase y
              conseguir al alumno. Tu precio no baja por eso.
            </div>

            {pr.propuesto ? (
              <div className="pp-aviso" style={{ marginTop: 12 }}>
                <span aria-hidden="true">⏳</span>
                <span>
                  Tu propuesta de{' '}
                  <strong>{pr.propuesto.toLocaleString('es-MX')} {moneda}</strong>{' '}
                  está con gerencia. Hasta que la aprueben sigue publicado el
                  precio de arriba.
                </span>
              </div>
            ) : null}

            <div className="pp-firmar" style={{ marginTop: 14 }}>
              <input
                className="pp-input"
                inputMode="decimal"
                maxLength={7}
                placeholder={`Ej. 450 ${moneda}`}
                value={pide}
                onChange={(e) => setPide(e.target.value)}
              />
              <button
                type="button"
                className="pp-btn pp-btn-ac"
                disabled={!puedePedir}
                onClick={() => proponer.mutate(cifra, {
                  onSuccess: () => setPide(''),
                })}
              >
                {proponer.isPending ? 'Enviando…' : 'Proponer'}
              </button>
            </div>
            <span className="pp-nota">
              No se publica solo: gerencia lo revisa primero.
            </span>

            {proponer.isError && (
              <div className="pp-error">{proponer.error?.message}</div>
            )}
          </>
        )}
      </section>
    </>
  );
}
